import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import {
  Box,
  Button,
  ButtonGroup,
  Slider,
  Stack,
  Tooltip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import * as React from "react";
import { DayWithTripIndicator } from "./DatePicker/DayWithTripIndicator";
import { SCHEDULE_MIN_DATE } from "../utils/dateConstants";
import { floorToQuarterHour, snapToQuarterHour } from "../utils/timeUtils";
import { SexyText } from "../widgets/SexyText/SexyText";

interface TimeRangeSliderProps {
  currentTime: Date;
  rangeStart: Date;
  rangeEnd: Date;
  onTimeChange: (time: Date) => void;
  onRangeChange: (start: Date, end: Date) => void;
  tripCountsByDate?: Map<string, number>;
  // Props for inline date/time controls
  showDateTimePicker?: boolean;
  showNowButton?: boolean;
  // Props for trip duration display
  selectedTripStart?: Date;
  selectedTripEnd?: Date;
  showTripDuration?: boolean;
  selectedTailNumber?: string;
  // Props for jump to trip buttons
  onJumpToNextTrip?: () => void;
  onJumpToPreviousTrip?: () => void;
  showTripJumpButtons?: boolean;
  hasNextTrip?: boolean;
  hasPreviousTrip?: boolean;
  // Props for View Schedule button
  onViewSchedule?: () => void;
  showViewScheduleButton?: boolean;
  viewScheduleDisabled?: boolean;
  // Compact mode (for backward compatibility with tests)
  compact?: boolean;
}

export const TimeRangeSlider: React.FC<TimeRangeSliderProps> = ({
  currentTime,
  rangeStart,
  rangeEnd,
  onTimeChange,
  onRangeChange,
  tripCountsByDate,
  showDateTimePicker = false,
  showNowButton = false,
  selectedTripStart,
  selectedTripEnd,
  showTripDuration = false,
  selectedTailNumber,
  onJumpToNextTrip,
  onJumpToPreviousTrip,
  showTripJumpButtons = false,
  hasNextTrip = false,
  hasPreviousTrip = false,
  onViewSchedule,
  showViewScheduleButton = false,
  viewScheduleDisabled = false,
  compact = false,
}) => {
  // State to track which range button is selected
  const [selectedRange, setSelectedRange] = React.useState<"1W" | "2W" | "1M">(
    "1W"
  );

  // Convert time to slider value (0-100 representing position within range)
  const timeToSliderValue = (time: Date): number => {
    const totalRange = rangeEnd.getTime() - rangeStart.getTime();
    const currentPosition = time.getTime() - rangeStart.getTime();
    return Math.max(0, Math.min(100, (currentPosition / totalRange) * 100));
  };

  // Convert slider value back to time
  const sliderValueToTime = (value: number): Date => {
    const totalRange = rangeEnd.getTime() - rangeStart.getTime();
    const timeOffset = (value / 100) * totalRange;
    return new Date(rangeStart.getTime() + timeOffset);
  };

  const currentSliderValue = timeToSliderValue(currentTime);

  // Counter for reliable flashing on range changes
  const [rangeChangeCounter, setRangeChangeCounter] = React.useState(0);

  React.useEffect(() => {
    setRangeChangeCounter((prev) => prev + 1);
  }, [rangeStart, rangeEnd]);

  // Calculate trip duration bar position and width
  const tripDurationBar = React.useMemo(() => {
    if (!showTripDuration || !selectedTripStart || !selectedTripEnd) {
      return null;
    }

    const startValue = timeToSliderValue(selectedTripStart);
    const endValue = timeToSliderValue(selectedTripEnd);

    // Calculate visible portion of the trip
    const visibleStartValue = Math.max(0, Math.min(100, startValue));
    const visibleEndValue = Math.max(0, Math.min(100, endValue));
    const visibleWidth = visibleEndValue - visibleStartValue;

    // Only show if trip has visible portion
    if (visibleWidth <= 0) {
      return null;
    }

    const result = {
      left: visibleStartValue,
      width: visibleWidth,
    };

    return result;
  }, [
    showTripDuration,
    selectedTripStart,
    selectedTripEnd,
    rangeStart,
    rangeEnd,
  ]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    if (typeof value === "number") {
      const newTime = sliderValueToTime(value);
      // Snap to 15-minute intervals when clicking/dragging
      const snappedTime = new Date(newTime);
      const minutes = Math.round(snappedTime.getMinutes() / 15) * 15;
      snappedTime.setMinutes(minutes, 0, 0);
      onTimeChange(snappedTime);
    }
  };

  // Handle keyboard navigation (arrow keys for 15-minute increments)
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const fifteenMinutes = 15 * 60 * 1000;
    let newTime: Date | null = null;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        newTime = new Date(currentTime.getTime() - fifteenMinutes);
        break;
      case "ArrowRight":
        event.preventDefault();
        newTime = new Date(currentTime.getTime() + fifteenMinutes);
        break;
      case "Home":
        event.preventDefault();
        newTime = new Date(rangeStart);
        break;
      case "End":
        event.preventDefault();
        newTime = new Date(rangeEnd);
        break;
    }

    if (newTime && newTime >= rangeStart && newTime <= rangeEnd) {
      onTimeChange(newTime);
    }
  };

  // Range control functions
  const setWeekRange = () => {
    setSelectedRange("1W");
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 3);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setDate(end.getDate() + 4);
    end.setHours(23, 59, 59, 999);

    onRangeChange(start, end);
  };

  const setTwoWeekRange = () => {
    setSelectedRange("2W");
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);

    onRangeChange(start, end);
  };

  const setMonthRange = () => {
    setSelectedRange("1M");
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 15);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setDate(end.getDate() + 15);
    end.setHours(23, 59, 59, 999);

    onRangeChange(start, end);
  };

  // Jump to now function for inline Now button
  const jumpToNow = () => {
    const now = new Date();
    onTimeChange(floorToQuarterHour(now));
  };

  const shiftTime = (minutes: number) => {
    const time = minutes * 60 * 1000;
    const newTime = new Date(currentTime.getTime() + time);
    onTimeChange(newTime);
  };

  // Main layout - vertical stacking with controls on top, slider on bottom
  return (
    <Stack
      direction="column"
      spacing={0}
      sx={{
        width: "100%",
      }}
    >
      {/* Controls row */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        {/* Range controls */}
        <ButtonGroup size={compact ? "small" : "small"} variant="outlined">
          <Button
            onClick={setWeekRange}
            variant={selectedRange === "1W" ? "contained" : "outlined"}
            sx={{ minWidth: "40px", fontSize: "0.7rem", px: 1 }}
          >
            1W
          </Button>
          <Button
            onClick={setTwoWeekRange}
            variant={selectedRange === "2W" ? "contained" : "outlined"}
            sx={{ minWidth: "40px", fontSize: "0.7rem", px: 1 }}
          >
            2W
          </Button>
          <Button
            onClick={setMonthRange}
            variant={selectedRange === "1M" ? "contained" : "outlined"}
            sx={{ minWidth: "40px", fontSize: "0.7rem", px: 1 }}
          >
            1M
          </Button>
        </ButtonGroup>

        {/* Inline DateTimePicker and Now button */}
        {showDateTimePicker && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              value={currentTime}
              onChange={(newDate) => {
                if (newDate) {
                  onTimeChange(snapToQuarterHour(newDate));
                }
              }}
              minDate={SCHEDULE_MIN_DATE}
              ampm={false}
              minutesStep={15}
              slots={{
                day: DayWithTripIndicator,
              }}
              slotProps={{
                day: {
                  tripCountsByDate,
                } as any,
                textField: {
                  size: "small",
                  sx: {
                    width: 210,
                    height: "27px !important",
                    minHeight: "unset !important",
                    boxSizing: "border-box",
                    "& .MuiPickersTextField-root": {
                      height: "27px !important",
                    },
                    "& .MuiInputBase-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                      fontSize: "0.75rem",
                      paddingTop: "0 !important",
                      paddingBottom: "0 !important",
                    },
                    "& .MuiPickersInputBase-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                    },
                    "& .MuiPickersOutlinedInput-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "3px 8px !important",
                      lineHeight: "1.2 !important",
                      height: "auto !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      top: "0 !important",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                      transform: "translate(8px, 6px) scale(1)",
                      "&.Mui-focused, &.MuiFormLabel-filled": {
                        transform: "translate(8px, -8px) scale(0.75)",
                      },
                    },
                    "& .MuiInputAdornment-root": {
                      height: "27px !important",
                      "& .MuiIconButton-root": {
                        padding: "2px !important",
                        "& .MuiSvgIcon-root": {
                          fontSize: "1rem !important",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        )}

        {/* Time navigation buttons group */}
        {(showNowButton || showTripJumpButtons) && (
          <ButtonGroup variant="outlined" size="small">
            {/* Previous trip button */}
            {showTripJumpButtons && (
              <Tooltip
                title={
                  !hasPreviousTrip
                    ? selectedTailNumber
                      ? `No earlier trips for ${selectedTailNumber}`
                      : "No earlier trips"
                    : selectedTailNumber
                    ? `Jump to previous trip for ${selectedTailNumber}`
                    : "Jump to previous trip"
                }
                arrow
              >
                <span>
                  <Button
                    onClick={onJumpToPreviousTrip}
                    disabled={!hasPreviousTrip || !onJumpToPreviousTrip}
                    startIcon={<FlightLandIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      fontSize: "0.75rem",
                      px: { xs: 0.75, sm: 1.25 },
                      minWidth: { xs: 40, sm: "auto" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& .MuiButton-startIcon": {
                        mr: { xs: 0, sm: 0.5 },
                        ml: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      Prev Trip
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Time shift buttons */}
            {showNowButton && (
              <>
                <Button
                  onClick={() => shiftTime(-60 * 24 * 7)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  -1w
                </Button>
                <Button
                  onClick={() => shiftTime(-15)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  -15m
                </Button>
                <Button
                  disabled={
                    currentTime.getTime() ===
                    floorToQuarterHour(new Date()).getTime()
                  }
                  variant="contained"
                  onClick={jumpToNow}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1.5,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Now
                </Button>
                <Button
                  onClick={() => shiftTime(15)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  +15m
                </Button>
                <Button
                  onClick={() => shiftTime(60 * 24 * 7)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  +1w
                </Button>
              </>
            )}

            {/* Next trip button */}
            {showTripJumpButtons && (
              <Tooltip
                title={
                  !hasNextTrip
                    ? selectedTailNumber
                      ? `No future trips for ${selectedTailNumber}`
                      : "No future trips"
                    : selectedTailNumber
                    ? `Jump to next trip for ${selectedTailNumber}`
                    : "Jump to next trip"
                }
                arrow
              >
                <span>
                  <Button
                    onClick={onJumpToNextTrip}
                    disabled={!hasNextTrip || !onJumpToNextTrip}
                    endIcon={<FlightTakeoffIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      fontSize: "0.75rem",
                      px: { xs: 0.75, sm: 1.25 },
                      minWidth: { xs: 40, sm: "auto" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& .MuiButton-endIcon": {
                        ml: { xs: 0, sm: 0.5 },
                        mr: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      Next Trip
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            )}
          </ButtonGroup>
        )}

        {/* View Schedule button */}
        {showViewScheduleButton && (
          <Button
            variant="outlined"
            startIcon={<CalendarViewMonthIcon />}
            onClick={onViewSchedule}
            disabled={viewScheduleDisabled}
            size="small"
            sx={{
              fontSize: "0.75rem",
              px: 1.5,
            }}
          >
            View Schedule
          </Button>
        )}
      </Stack>

      {/* Slider row */}
      <Box
        sx={{
          width: "100%",
          pt: 1.25, // Add padding-top to make room for trip duration bar (~10px)
          overflow: "visible", // Allow bar to show above
          position: "relative",
        }}
      >
        {/* Trip duration bar - positioned within padding area */}
        {tripDurationBar &&
          (() => {
            return (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    top: 15,
                    left: `calc(${tripDurationBar.left}%)`,
                    width: `${tripDurationBar.width}%`,
                    height: 6,
                    backgroundColor: "success.main",
                    zIndex: 0,
                  }}
                  title="Selected aircraft trip duration"
                />
              </>
            );
          })()}
        <Slider
          value={currentSliderValue}
          onChange={handleSliderChange}
          onKeyDown={handleKeyDown}
          min={0}
          max={100}
          step={0.1}
          marks={(() => {
            const marks = [];
            const rangeInDays = (rangeEnd.getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000);

            // Determine step size based on range
            let dayStep = 1;
            if (rangeInDays > 14) {
              dayStep = Math.ceil(rangeInDays / 7); // Show ~7 markers for longer ranges
            } else if (rangeInDays > 7) {
              dayStep = 2; // Every other day for 2 week range
            }

            // Always include the start
            marks.push({
              value: 0,
              label: (
                <SexyText
                  value={rangeChangeCounter}
                  text={rangeStart.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                  bidirectional={true}
                />
              ),
            });

            // Add day boundary markers
            const currentDate = new Date(rangeStart);
            currentDate.setHours(0, 0, 0, 0);
            currentDate.setDate(currentDate.getDate() + dayStep);

            while (currentDate < rangeEnd) {
              const sliderValue = timeToSliderValue(currentDate);
              // Only add if not too close to start or end
              if (sliderValue > 5 && sliderValue < 95) {
                marks.push({
                  value: sliderValue,
                  label: (
                    <SexyText
                      value={rangeChangeCounter}
                      text={currentDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                      bidirectional={true}
                    />
                  ),
                });
              }
              currentDate.setDate(currentDate.getDate() + dayStep);
            }

            // Always include the end
            marks.push({
              value: 100,
              label: (
                <SexyText
                  value={rangeChangeCounter}
                  text={rangeEnd.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                  bidirectional={true}
                />
              ),
            });

            return marks;
          })()}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => {
            const date = sliderValueToTime(value);
            return format(date, "MMM d, HH:mm");
          }}
          getAriaValueText={(value) => {
            const date = sliderValueToTime(value);
            return format(date, "MMM d, HH:mm");
          }}
          aria-label="Time selection slider"
          sx={{
            height: 6,
            "& .MuiSlider-track": {
              backgroundColor: "primary.main",
              border: "none",
            },
            "& .MuiSlider-rail": {
              backgroundColor: "primary.main",
              borderRadius: 0,
              opacity: 0.38,
            },
            "& .MuiSlider-mark": {
              backgroundColor: "text.primary",
              height: 8,
              width: 2,
              opacity: 1,
            },
            "& .MuiSlider-markLabel": {
              fontSize: "0.7rem",
              transform: "translateX(-50%)",
              top: 24,
              color: "text.secondary",
            },
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              backgroundColor: "primary.main",
              border: "3px solid",
              borderColor: "background.paper",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              cursor: "grab",
              zIndex: 1,
              "&:hover": {
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              },
              "&:focus, &.Mui-focusVisible": {
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              },
              "&.Mui-active": {
                cursor: "grabbing !important",
                boxShadow: "0 3px 8px rgba(0,0,0,0.3) !important",
              },
            },
            ".MuiSlider-rail": {
              opacity: 1,
            },
          }}
        />
      </Box>
    </Stack>
  );
};
